<template>
  <aside
    v-if="userQuestions.length >= 2"
    class="qnav"
    :class="{ 'qnav-expanded': isHover }"
    data-component-id="chat--qnav--root"
    @mouseenter="isHover = true"
    @mouseleave="isHover = false"
  >
    <div class="qnav-header" :title="`Pertanyaan ${activeQIndex || 1} dari ${userQuestions.length}`">
      <span class="qnav-header-label">Q</span>
      <span class="qnav-header-count">
        <span class="qnav-header-current">{{ activeQIndex || 1 }}</span>
        <span class="qnav-header-sep" aria-hidden="true">/</span>
        <span class="qnav-header-total">{{ userQuestions.length }}</span>
      </span>
    </div>

    <!-- Pinned active pill: always visible, auto-replaced as activeQIndex changes -->
    <button
      v-if="activePill"
      class="qnav-pill qnav-pill-active qnav-pinned"
      :title="`Pertanyaan aktif — ${activePill.preview}`"
      :data-component-id="`chat--qnav--pinned-q${activePill.qIndex}`"
      @click="$emit('jump', activePill.qIndex)"
    >
      <span class="qnav-pill-num">Q{{ activePill.qIndex }}</span>
      <span class="qnav-pill-text">{{ activePill.preview }}</span>
    </button>

    <div v-if="activePill" class="qnav-divider" aria-hidden="true"></div>

    <div ref="listEl" class="qnav-list">
      <button
        v-for="q in userQuestions"
        :key="q.id"
        :ref="el => bindPillRef(q.qIndex, el)"
        class="qnav-pill"
        :class="{ 'qnav-pill-active': q.qIndex === activeQIndex }"
        :title="q.preview"
        :data-component-id="`chat--qnav--pill-q${q.qIndex}`"
        @click="$emit('jump', q.qIndex)"
      >
        <span class="qnav-pill-num">Q{{ q.qIndex }}</span>
        <span class="qnav-pill-text">{{ q.preview }}</span>
      </button>
    </div>

    <div v-if="!isHover" class="qnav-hint" aria-hidden="true">arahkan</div>
  </aside>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'

const props = defineProps({
  messages:        { type: Array,   required: true },
  scrollContainer: { type: Object,  default: null }, // HTMLElement (raw)
})
const emit = defineEmits(['jump', 'active-change'])

const isHover = ref(false)
const activeQIndex = ref(1)
const listEl = ref(null)
const pillRefs = new Map()

function bindPillRef(qIndex, el) {
  if (el) pillRefs.set(qIndex, el)
  else pillRefs.delete(qIndex)
}

const userQuestions = computed(() => {
  let n = 0
  return props.messages
    .filter(m => m && m.role === 'user' && m.content && !m.error)
    .map(m => {
      n++
      const text = String(m.content).replace(/\s+/g, ' ').trim()
      return {
        id: m.id,
        qIndex: n,
        preview: text.length > 90 ? text.slice(0, 87) + '…' : text || '(kosong)',
      }
    })
})

const activePill = computed(() => {
  if (!userQuestions.value.length) return null
  return userQuestions.value.find(q => q.qIndex === activeQIndex.value)
    || userQuestions.value[0]
})

let _scrollHandler = null
let _rafPending = false

function scrollSpy() {
  const root = props.scrollContainer
  if (!root) return
  const anchors = root.querySelectorAll('[data-qid]')
  if (!anchors.length) return

  const containerTop = root.getBoundingClientRect().top
  const trigger = containerTop + 90 // 90px below top of scroll area
  let active = userQuestions.value[0]?.qIndex || 1
  let activeAnchor = null
  anchors.forEach(el => {
    const top = el.getBoundingClientRect().top
    if (top <= trigger) {
      const qid = parseInt(el.getAttribute('data-qid') || '0', 10)
      if (qid > 0) { active = qid; activeAnchor = el }
    }
  })
  // outOfView = active question's bubble has fully scrolled above the
  // viewport top (so the user is reading the answer with no question in sight).
  let outOfView = false
  if (!activeAnchor) {
    activeAnchor = root.querySelector(`[data-qid="${active}"]`)
  }
  if (activeAnchor) {
    const r = activeAnchor.getBoundingClientRect()
    outOfView = r.bottom < containerTop + 4
  }
  emit('active-change', { qIndex: active, outOfView })

  if (active !== activeQIndex.value) {
    activeQIndex.value = active
    // Auto-scroll the pill list so the active pill stays in view.
    const pill = pillRefs.get(active)
    if (pill && listEl.value) {
      const pTop = pill.offsetTop
      const pBottom = pTop + pill.offsetHeight
      const lTop = listEl.value.scrollTop
      const lBottom = lTop + listEl.value.clientHeight
      if (pTop < lTop)        listEl.value.scrollTop = Math.max(0, pTop - 10)
      else if (pBottom > lBottom) listEl.value.scrollTop = pBottom - listEl.value.clientHeight + 10
    }
  }
}

function onScroll() {
  if (_rafPending) return
  _rafPending = true
  requestAnimationFrame(() => {
    _rafPending = false
    scrollSpy()
  })
}

function attachListener() {
  detachListener()
  const root = props.scrollContainer
  if (!root) return
  _scrollHandler = onScroll
  root.addEventListener('scroll', _scrollHandler, { passive: true })
  // Run once after attach so initial state matches.
  nextTick(scrollSpy)
}

function detachListener() {
  if (!_scrollHandler) return
  const root = props.scrollContainer
  if (root) root.removeEventListener('scroll', _scrollHandler)
  _scrollHandler = null
}

watch(() => props.scrollContainer, () => attachListener(), { immediate: false })
watch(() => userQuestions.value.length, () => nextTick(scrollSpy))

onMounted(() => {
  // Defer to ensure scrollContainer (parent's ref) is mounted.
  nextTick(attachListener)
})
onBeforeUnmount(detachListener)
</script>

<style scoped>
.qnav {
  position: absolute;
  top: 56px;
  left: 6px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 44px;
  pointer-events: auto;
  /* Solid panel background so content underneath never bleeds through —
     especially noticeable in the gaps between pills when expanded. */
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 2px 8px -2px rgba(0, 0, 0, 0.35);
  transition: width 0.18s ease, box-shadow 0.18s ease;
}
.qnav-expanded {
  width: 252px;
  box-shadow: 0 8px 22px -6px rgba(0, 0, 0, 0.55);
}

/* ── Header chip: vertical stack, intentional ─────────────────────────── */
.qnav-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 4px 4px 5px;
  border-radius: 8px;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--accent, #3b82f6) 9%, transparent),
    transparent
  );
  border: 1px solid var(--border-soft, var(--border-light));
  font-variant-numeric: tabular-nums;
  user-select: none;
  line-height: 1;
}
.qnav-header-label {
  color: var(--accent, var(--accent-blue, #3b82f6));
  font-weight: 700;
  font-size: 8.5px;
  letter-spacing: 1px;
  text-transform: uppercase;
  opacity: 0.9;
}
.qnav-header-count {
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
  font-weight: 700;
  color: var(--text-base, var(--text-body));
  font-size: 11px;
}
.qnav-header-current {
  color: var(--accent, var(--accent-blue, #3b82f6));
}
.qnav-header-sep {
  opacity: 0.45;
  font-weight: 500;
  font-size: 10px;
  margin: 0 1px;
}
.qnav-header-total {
  color: var(--text-muted, var(--text-ghost, #888));
  font-weight: 600;
  font-size: 10px;
}

/* ── Pinned active pill: always visible above the scrollable list ──────── */
.qnav-pinned {
  /* Sits between header and divider; reuses .qnav-pill base styles. */
  flex-shrink: 0;
  /* Subtle "pinned" elevation — gradient accent + soft glow so it reads as
     a sticky/pinned indicator, not just another active pill. */
  background:
    linear-gradient(
      180deg,
      var(--accent, var(--accent-blue, #3b82f6)),
      color-mix(in srgb, var(--accent, #3b82f6) 88%, #000)
    ) !important;
  box-shadow:
    0 0 0 1px var(--accent, var(--accent-blue, #3b82f6)) inset,
    0 2px 10px -2px color-mix(in srgb, var(--accent, #3b82f6) 70%, transparent),
    0 0 0 2px color-mix(in srgb, var(--accent, #3b82f6) 20%, transparent) !important;
  position: relative;
}
.qnav-pinned::before {
  /* Tiny pin dot at the top-left to signal "pinned" status. */
  content: '';
  position: absolute;
  top: 3px;
  right: 4px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 2px color-mix(in srgb, #fff 25%, transparent);
  opacity: 0.85;
  pointer-events: none;
}

.qnav-divider {
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--border-soft, var(--border-light)),
    transparent
  );
  margin: 1px 2px 0;
  opacity: 0.7;
  flex-shrink: 0;
}

/* ── Pill list: hide scrollbar when collapsed, show clean one expanded ─── */
.qnav-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  padding: 2px 0;
  /* Collapsed: scrollbar hidden so it never crops the Q numbers. */
  scrollbar-width: none;
}
.qnav-list::-webkit-scrollbar { width: 0; height: 0; }

.qnav-expanded .qnav-list {
  padding: 2px 2px 2px 0;
  scrollbar-width: thin;
  scrollbar-gutter: stable;
}
.qnav-expanded .qnav-list::-webkit-scrollbar { width: 5px; }
.qnav-expanded .qnav-list::-webkit-scrollbar-thumb {
  background: var(--border-soft, var(--border-light));
  border-radius: 3px;
}
.qnav-expanded .qnav-list::-webkit-scrollbar-thumb:hover {
  background: var(--accent, var(--accent-blue, #3b82f6));
}

/* ── Pill: centered Q in collapsed, full text in expanded ──────────────── */
.qnav-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  padding: 5px 4px;
  border-radius: 7px;
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background 0.12s, border-color 0.12s, color 0.12s, transform 0.08s, box-shadow 0.12s;
  white-space: nowrap;
  overflow: hidden;
  min-height: 26px;
}
.qnav-expanded .qnav-pill {
  justify-content: flex-start;
  padding: 5px 8px;
}
.qnav-pill:hover {
  background: var(--bg-hover, var(--bg-hover));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.qnav-pill:active { transform: scale(0.97); }
.qnav-pill-active {
  /* Solid accent — no rgba transparency, so it always reads cleanly. */
  background: var(--accent, var(--accent-blue, #3b82f6));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  color: #fff;
  box-shadow:
    0 0 0 1px var(--accent, var(--accent-blue, #3b82f6)) inset,
    0 1px 4px -1px color-mix(in srgb, var(--accent, #3b82f6) 60%, transparent);
}
.qnav-pill-active .qnav-pill-num,
.qnav-pill-active .qnav-pill-text {
  color: #fff;
}

.qnav-pill-num {
  font-weight: 700;
  font-size: 10px;
  letter-spacing: 0.2px;
  color: var(--text-muted, var(--text-ghost, #888));
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
  min-width: 22px;
  text-align: center;
}
.qnav-pill:hover .qnav-pill-num {
  color: var(--accent, var(--accent-blue, #3b82f6));
}
.qnav-pill-active:hover .qnav-pill-num { color: #fff; }

.qnav-pill-text {
  flex: 1 1 auto;
  text-overflow: ellipsis;
  overflow: hidden;
  opacity: 0;
  max-width: 0;
  transition: opacity 0.16s ease 0.04s, max-width 0.18s ease;
}
.qnav-expanded .qnav-pill-text {
  opacity: 1;
  max-width: 220px;
}

.qnav-hint {
  align-self: center;
  font-size: 8.5px;
  letter-spacing: 0.5px;
  color: var(--text-dim, #6b7a8d);
  text-transform: uppercase;
  opacity: 0.55;
  pointer-events: none;
  user-select: none;
  padding-top: 1px;
}
</style>
